import { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../ui/Dialog';
import { Tooltip } from '../ui/Tooltip';
import { apiClient } from '../../lib/api-client';
import { cn } from '../../lib/utils';
import { TenantLogo } from '../tenants/TenantLogo';

interface TenantInfo {
  code: string;
  name: string;
  description: string | null;
  logoUrl?: string | null;
  color?: string | null;
}

export function TenantSelector() {
  const { accessibleTenants, selectedTenants, setSelectedTenants } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [tempSelection, setTempSelection] = useState<string[]>(selectedTenants);
  const [tenantInfo, setTenantInfo] = useState<Record<string, TenantInfo>>({});

  // Fetch tenant details when accessible tenants change
  useEffect(() => {
    const fetchTenantInfo = async () => {
      try {
        const response = await apiClient.getTenants();
        const tenants = response.tenants || [];
        const infoMap: Record<string, TenantInfo> = {};
        
        for (const tenant of tenants) {
          // Map by lowercase code for matching with accessibleTenants
          infoMap[tenant.code.toLowerCase()] = {
            code: tenant.code,
            name: tenant.name,
            description: tenant.description,
            logoUrl: tenant.logoUrl,
            color: tenant.color,
          };
        }
        
        setTenantInfo(infoMap);
      } catch (error) {
        console.error('Failed to fetch tenant info:', error);
      }
    };
    
    if (accessibleTenants.length > 0) {
      fetchTenantInfo();
    }
  }, [accessibleTenants]);

  // Initialize temp selection when dialog opens
  const handleOpen = () => {
    setTempSelection(selectedTenants.length === 0 ? accessibleTenants : selectedTenants);
    setIsOpen(true);
  };

  const toggleTenant = (tenant: string) => {
    if (tempSelection.includes(tenant)) {
      setTempSelection(tempSelection.filter(t => t !== tenant));
    } else {
      setTempSelection([...tempSelection, tenant]);
    }
  };

  const selectAll = () => {
    setTempSelection(accessibleTenants);
  };

  const clearAll = () => {
    setTempSelection([]);
  };

  const handleOk = () => {
    setSelectedTenants(tempSelection.length === 0 ? accessibleTenants : tempSelection);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempSelection(selectedTenants.length === 0 ? accessibleTenants : selectedTenants);
    setIsOpen(false);
  };

  const selectedCount = selectedTenants.length === 0 || selectedTenants.length === accessibleTenants.length
    ? accessibleTenants.length
    : selectedTenants.length;

  return (
    <>
      <Tooltip content="Select Tenants" side="bottom">
        <button
          onClick={handleOpen}
          aria-label="Select Tenants"
          className={cn(
            'flex items-center justify-center p-2 transition-colors relative',
            'hover:bg-gray-100 dark:hover:bg-gray-800'
          )}
        >
          <TenantLogo
            tenant={{
              code: selectedCount > 1 ? '' : (tenantInfo[selectedTenants[0]?.toLowerCase()]?.code ?? ''),
              color: '#64748b',
              logoUrl: selectedCount === 1 ? tenantInfo[selectedTenants[0]?.toLowerCase()]?.logoUrl : null,
            }}
            size="md"
            className="pointer-events-none"
          />
          {selectedCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-blue-600 dark:bg-blue-500 text-white text-xs font-medium flex items-center justify-center pointer-events-none">
              {selectedCount}
            </span>
          )}
        </button>
      </Tooltip>

      <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
        <DialogContent maxWidth="md">
          <DialogHeader>
            <DialogTitle>Select Tenants</DialogTitle>
          </DialogHeader>
          
          <DialogBody>
            <div className="space-y-4">
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={selectAll}
                  className="flex-1 text-gray-700 dark:text-gray-300"
                >
                  Select All
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={clearAll}
                  className="flex-1 text-gray-700 dark:text-gray-300"
                >
                  Clear
                </Button>
              </div>
            
              <div className="max-h-[400px] overflow-y-auto space-y-1">
                {accessibleTenants.map((tenantCode) => {
                  const isSelected = tempSelection.includes(tenantCode) || tempSelection.length === 0;
                  const info = tenantInfo[tenantCode];
                  const displayName = info?.name || tenantCode.toUpperCase();
                  
                  return (
                    <button
                      key={tenantCode}
                      onClick={() => toggleTenant(tenantCode)}
                      className={cn(
                        'w-full flex items-center justify-between px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-900 dark:text-gray-100',
                        isSelected && 'bg-blue-50 dark:bg-blue-900/20'
                      )}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <TenantLogo
                          tenant={{
                            code: info?.code ?? tenantCode,
                            name: displayName,
                            color: info?.color,
                            logoUrl: info?.logoUrl,
                          }}
                          size="md"
                        />
                        <div className="flex flex-col items-start min-w-0">
                        <span className="font-medium truncate">{displayName}</span>
                        {info && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                            {info.code}
                          </span>
                        )}
                        </div>
                      </div>
                      {isSelected && <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleOk}>
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

